import type { ItemId } from '@/data/items';
import type { NpcId, Posting } from '@/data/npcs';
import type { Season } from '@/time/clock';

/**
 * Four days a year when everyone is somewhere else.
 *
 * A festival is a date, a place, and a reason to bring something. It is
 * content rather than machinery: the schedule override is a posting like any
 * other, so a festival does not need the world to work differently on the day
 * — it needs the same world with everyone standing somewhere new.
 *
 * One per season, on a day far enough in that a new farm does not open on one
 * and far enough from the end that missing it means waiting, not never.
 */

export type FestivalId = 'blossomMarket' | 'longestDay' | 'harvestSupper' | 'lanternNight';

export interface Festival {
  readonly id: FestivalId;
  readonly name: string;
  readonly season: Season;
  /** Day of the season, one-based, as the clock reports it. */
  readonly day: number;
  /** Where it happens, and when it starts and ends. */
  readonly area: string;
  readonly fromHour: number;
  readonly toHour: number;
  /** Who is hosting, and where each person stands. */
  readonly host: NpcId;
  readonly postings: Readonly<Record<string, { x: number; y: number }>>;
  /** What the HUD says instead of the date. */
  readonly blurb: string;
  /**
   * What the contest wants.
   *
   * A kind rather than a specific item, so any turnip counts and the question
   * is what you bring rather than whether you own the one right thing.
   */
  readonly wants: (item: ItemId) => boolean;
  readonly wantsDescription: string;
  /** What the host says when you have nothing to enter. */
  readonly empty: string;
}

const isCrop = (item: ItemId): boolean => item.startsWith('crop:');
const isFish = (item: ItemId): boolean => item.startsWith('fish:');

export const FESTIVALS: readonly Festival[] = [
  {
    id: 'blossomMarket',
    name: 'Blossom Market',
    season: 'spring',
    day: 12,
    area: 'farm',
    fromHour: 9,
    toHour: 18,
    host: 'pell',
    // Around the crossroads, either side of the board and the cart.
    postings: { pell: { x: 24, y: 16 }, mira: { x: 20, y: 16 } },
    blurb: 'Blossom Market',
    wants: isCrop,
    wantsDescription: 'the best of the first crop',
    empty: 'A market wants goods. Bring me something you grew.',
  },
  {
    id: 'longestDay',
    name: 'The Longest Day',
    season: 'summer',
    day: 20,
    area: 'coast',
    fromHour: 10,
    toHour: 20,
    host: 'mira',
    postings: { mira: { x: 14, y: 24 }, pell: { x: 18, y: 24 } },
    blurb: 'The Longest Day',
    wants: isFish,
    wantsDescription: 'the heaviest thing out of the water',
    empty: 'Everyone brings a fish. That is the entire festival, really.',
  },
  {
    id: 'harvestSupper',
    name: 'Harvest Supper',
    season: 'autumn',
    day: 16,
    area: 'farm',
    fromHour: 12,
    toHour: 22,
    host: 'pell',
    postings: { pell: { x: 24, y: 16 }, mira: { x: 20, y: 16 } },
    blurb: 'Harvest Supper',
    wants: isCrop,
    wantsDescription: 'something for the table',
    empty: 'It is a supper. You are meant to contribute to the supper.',
  },
  {
    id: 'lanternNight',
    name: 'Lantern Night',
    season: 'winter',
    day: 24,
    area: 'coast',
    fromHour: 16,
    toHour: 23,
    host: 'mira',
    postings: { mira: { x: 14, y: 24 }, pell: { x: 18, y: 24 } },
    blurb: 'Lantern Night',
    wants: isFish,
    wantsDescription: 'a fish that shines',
    empty: 'No lantern, no fish. Stay for the light anyway.',
  },
];

/** The festival on a given date, if there is one. */
export function festivalOn(season: Season, dayOfSeason: number): Festival | null {
  return (
    FESTIVALS.find(
      (festival) => festival.season === season && festival.day === dayOfSeason,
    ) ?? null
  );
}

/**
 * Where someone stands during a festival, or null if they are not invited.
 *
 * Returns a Posting so callers treat festival attendance exactly like any
 * other entry in a schedule — the day does not get its own code path.
 */
export function festivalPosting(festival: Festival, id: NpcId, hour: number): Posting | null {
  const spot = festival.postings[id];
  if (!spot) return null;
  if (hour < festival.fromHour || hour >= festival.toHour) return null;

  return { fromHour: festival.fromHour, area: festival.area, x: spot.x, y: spot.y };
}
